import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { announce, ensureAnnouncer, writeRegion } from '@theme/ca-announcer';
import { loadBody, VariantSheet } from '@theme/ca-variant-sheet';
import { addToCart, cartSectionIds, preorderProperties, themeRoutes } from '@theme/ca-cart-add';

const SOURCE = 'ca-quick-add';
const DWELL_MS = 4000;
const DWELL_LONG_MS = 6000;
const ANNOUNCE_FALLBACK_MS = 800;
const RESUME_MIN_MS = 1500;
const LEAVE_MS = 260;
const QUEUE_WAIT_MS = 15000;
const OPEN_CLASS = 'is-ca-quick-add-open';

/** @type {Record<string, any> | null} */
let configCache = null;

/** @type {Promise<void>} */
let cartQueue = Promise.resolve();

/** @returns {Record<string, any>} */
function config() {
  if (configCache) return configCache;
  const el = document.getElementById('CaQuickAddConfig');
  try {
    configCache = el ? JSON.parse(el.textContent || '{}') : {};
  } catch (error) {
    configCache = {};
  }
  return /** @type {Record<string, any>} */ (configCache);
}

/**
 * @param {number} cents
 * @returns {string}
 */
function money(cents) {
  const cfg = config();
  if (!cfg.moneyFormat) return '';
  try {
    return formatMoney(cents, cfg.moneyFormat, cfg.currency || 'USD');
  } catch (error) {
    return '';
  }
}

/** @returns {Promise<any>} */
function fetchCart() {
  return fetch(`${themeRoutes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

const ANNOUNCE_DELAY_MS = 60;

class QuickAddSheet {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {VariantSheet | null} */
  sheet = null;
  /** @type {HTMLElement | null} */
  alert = null;
  /** @type {CaQuickAddComponent | null} */
  owner = null;
  lockedScroll = false;
  requestId = 0;

  ensure() {
    if (this.sheet) return;
    const dialog = document.getElementById('CaQuickAddSheet');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.querySelector('.cart-upsell__sheet-panel') || !dialog.querySelector('[data-sheet-body]')) return;
    this.dialog = dialog;
    this.sheet = new VariantSheet(dialog, {
      policy: 'notify',
      onConfirm: ({ pill }) => this.confirm(pill),
      onClose: () => this.onClose(),
    });
  }

  /**
   * @param {CaQuickAddComponent} owner
   */
  async open(owner) {
    this.ensure();
    const url = owner.dataset.sheetUrl;
    if (!url) return;
    if (!this.sheet || !this.dialog) {
      if (owner.dataset.productUrl) window.location.href = owner.dataset.productUrl;
      return;
    }

    const token = ++this.requestId;
    owner.setBusy(true);
    /** @type {Element} */
    let options;
    try {
      options = await loadBody(url);
    } catch (error) {
      owner.setBusy(false);
      if (token === this.requestId && owner.dataset.productUrl) window.location.href = owner.dataset.productUrl;
      return;
    }
    owner.setBusy(false);
    if (token !== this.requestId || !owner.isConnected) return;

    this.owner = owner;
    const hint = document.createElement('p');
    hint.id = 'CaQuickAddSheetHint';
    hint.className = 'ca-quick-add-sheet__hint';
    hint.setAttribute('role', 'alert');
    options.querySelector('.ca-variant-options__actions')?.prepend(hint);
    options.querySelector('.cart-upsell__pills')?.setAttribute('aria-describedby', hint.id);
    this.alert = hint;
    this.dialog.setAttribute('aria-label', owner.dataset.productTitle || '');
    this.sheet.open(options, {
      title: owner.dataset.productTitle || '',
      preorderLabel: config().preorder,
      onSelect: () => {
        hint.textContent = '';
      },
      onBlocked: () => this.promptChoice(),
    });
    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.lockedScroll = true;
    }
  }

  onClose() {
    if (this.lockedScroll) {
      document.documentElement.removeAttribute('scroll-lock');
      this.lockedScroll = false;
    }
    this.alert = null;
    this.owner?.focusTrigger(true);
  }

  /**
   * @param {HTMLElement | null} chosen
   */
  confirm(chosen) {
    if (!this.owner || !chosen) return;
    this.owner.add({
      variantId: chosen.dataset.variantId || '',
      quantity: parseInt(chosen.dataset.variantQuantity || '1', 10) || 1,
      properties: preorderProperties(chosen),
      variantTitle: (chosen.firstChild?.textContent || '').trim(),
    });
  }

  promptChoice() {
    const root = /** @type {HTMLElement | null} */ (this.sheet?.root() || null);
    if (!root || root.querySelector('[data-variant-pill][aria-checked="true"]')) return;
    const pills = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-variant-pill]')));
    const first = pills.find((pill) => pill.dataset.variantAvailable !== 'false') || pills[0];
    root.querySelector('.cart-upsell__pills')?.setAttribute('data-needs-choice', '');
    writeRegion(this.alert, root.dataset.chooseHint || 'Choose an option first.', ANNOUNCE_DELAY_MS);
    if (!first) return;
    pills.forEach((pill) => {
      pill.tabIndex = pill === first ? 0 : -1;
    });
    first.focus();
  }
}

const sheet = new QuickAddSheet();

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [trigger]
 * @property {HTMLElement} [panel]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [detail]
 * @property {HTMLElement} [ship]
 * @property {HTMLElement} [track]
 * @property {HTMLElement} [fill]
 * @property {HTMLAnchorElement} [link]
 *
 * @extends {Component<Refs>}
 */
class CaQuickAddComponent extends Component {
  /** @type {CaQuickAddComponent | null} */
  static active = null;

  #timer = 0;
  #deadline = 0;
  #remaining = 0;
  #autoDismiss = false;
  #hovering = false;
  #held = false;
  #touchUsed = false;
  #quietTrigger = false;
  #busy = false;
  #soldOut = false;
  #leaveTimer = 0;
  #addToken = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#reset();
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerenter', this.#onPointerEnter, true);
    this.addEventListener('pointerleave', this.#onPointerLeave, true);
    this.addEventListener('pointerdown', this.#onPointerDown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('pointerenter', this.#onPointerEnter, true);
    this.removeEventListener('pointerleave', this.#onPointerLeave, true);
    this.removeEventListener('pointerdown', this.#onPointerDown, true);
    this.#release();
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#leaveTimer);
    this.#timer = 0;
    this.#autoDismiss = false;
    this.#hovering = false;
    this.#addToken += 1;
    if (CaQuickAddComponent.active === this) CaQuickAddComponent.active = null;
  }

  #reset() {
    const { panel, trigger } = this.refs;
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#leaveTimer);
    this.#release();
    this.#timer = 0;
    this.#autoDismiss = false;
    this.#hovering = false;
    this.#busy = false;
    this.#soldOut = false;
    if (panel && (!panel.hidden || panel.classList.contains('is-open'))) {
      panel.classList.remove('is-open');
      panel.hidden = true;
    }
    if (this.hasAttribute('data-state')) this.removeAttribute('data-state');
    this.classList.remove('is-busy');
    if (trigger) {
      trigger.removeAttribute('aria-busy');
      if (trigger.dataset.state && trigger.dataset.state !== 'default') this.#setTrigger('default');
    }
    this.#item()?.classList.remove(OPEN_CLASS);
  }

  /**
   * @param {Event} event
   */
  handleTrigger(event) {
    event.preventDefault();
    const trigger = this.refs.trigger;
    if (!trigger || this.#busy || trigger.getAttribute('aria-disabled') === 'true') return;

    if (trigger.hasAttribute('data-choose')) {
      CaQuickAddComponent.active?.dismiss(true);
      sheet.open(this);
      return;
    }

    this.add({
      variantId: trigger.dataset.variantId || '',
      quantity: parseInt(trigger.dataset.quantity || '1', 10) || 1,
      properties: preorderProperties(trigger),
      variantTitle: '',
    });
  }

  handleClose() {
    this.dismiss();
    this.focusTrigger();
  }

  /**
   * @param {Event} event
   */
  handleLink(event) {
    const link = this.refs.link;
    if (!link || link.dataset.cart !== 'true') return;
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    this.dismiss(true);
    drawer.open();
  }

  /**
   * @param {boolean} on
   */
  setBusy(on) {
    this.#busy = on;
    this.classList.toggle('is-busy', on);
    const trigger = this.refs.trigger;
    if (!trigger) return;
    const state = trigger.dataset.state;
    if (on) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.setAttribute('aria-disabled', 'true');
    } else {
      trigger.removeAttribute('aria-busy');
      if (state !== 'added' && state !== 'soldout') trigger.removeAttribute('aria-disabled');
    }
  }

  /**
   * @param {boolean} [quiet]
   */
  focusTrigger(quiet = false) {
    this.#quietTrigger = quiet;
    this.refs.trigger?.focus({ preventScroll: true });
  }

  /**
   * @param {{ variantId: string, quantity: number, properties: Record<string, string> | null, variantTitle: string }} payload
   */
  add(payload) {
    if (!payload.variantId || this.#busy) return;
    this.setBusy(true);
    this.dismiss(true);
    const token = ++this.#addToken;
    announce(`${this.dataset.productTitle || ''}: ${config().adding || 'Adding...'}`, { delay: ANNOUNCE_DELAY_MS });
    const run = cartQueue.then(() => this.#add(payload, token));
    const settled = run.catch((error) => {
      this.setBusy(false);
      if (window.console) console.warn('[ca-quick-add]', error);
    });
    cartQueue = cartQueue.then(() =>
      Promise.race([settled, new Promise((resolve) => window.setTimeout(resolve, QUEUE_WAIT_MS))])
    );
  }

  /**
   * @param {{ variantId: string, quantity: number, properties: Record<string, string> | null, variantTitle: string }} payload
   * @param {number} token
   */
  async #add({ variantId, quantity, properties, variantTitle }, token) {
    const drawerMode = this.dataset.mode === 'drawer';
    const sectionIds = cartSectionIds();

    /** @type {Response} */
    let response;
    /** @type {any} */
    let addData = null;
    try {
      ({ response, data: addData } = await addToCart({
        id: Number(variantId),
        quantity,
        properties,
        sections: sectionIds.length ? sectionIds : null,
      }));
    } catch (error) {
      this.setBusy(false);
      if (token === this.#addToken) this.#fail('error', variantId, '');
      return;
    }

    if (!response.ok) {
      const description = response.status === 422 ? String(addData?.description || '') : '';
      const soldOut = response.status === 422 ? await this.#variantSoldOut(variantId) : false;
      this.setBusy(false);
      if (token === this.#addToken) this.#fail(soldOut ? 'soldout' : 'error', variantId, description);
      return;
    }

    /** @type {any} */
    const cart = sectionIds.length ? null : await fetchCart();
    this.setBusy(false);

    /** @type {Record<string, any>} */
    const eventData = {
      source: SOURCE,
      sections: addData?.sections,
      productId: this.dataset.productId,
      variantId: String(variantId),
    };
    if (typeof cart?.item_count === 'number') eventData.itemCount = cart.item_count;
    document.dispatchEvent(new CartAddEvent(undefined, SOURCE, eventData));

    this.#track(variantId, quantity, drawerMode ? 'drawer' : 'panel');

    if (token !== this.#addToken) return;

    if (drawerMode) {
      const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        this.#announceAdded(variantTitle, '');
        return;
      }
    }

    const line = Array.isArray(addData?.items) ? addData.items[0] : addData;
    const addedCents = typeof line?.final_price === 'number' ? line.final_price * quantity : 0;
    this.#showAdded(variantTitle);
    this.#fillShipping(token, addedCents, variantTitle, cart);
  }

  /**
   * @param {boolean} [immediate]
   */
  dismiss(immediate = false) {
    window.clearTimeout(this.#timer);
    this.#timer = 0;
    this.#remaining = 0;
    this.#autoDismiss = false;
    this.#hovering = false;
    this.#release();

    const panel = this.refs.panel;
    const hadFocus = !!panel && panel.contains(document.activeElement);

    this.removeAttribute('data-state');
    this.#item()?.classList.remove(OPEN_CLASS);
    if (CaQuickAddComponent.active === this) CaQuickAddComponent.active = null;
    this.#setTrigger(this.#soldOut ? 'soldout' : 'default');

    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    if (hadFocus) this.focusTrigger();

    window.clearTimeout(this.#leaveTimer);
    if (immediate) {
      panel.hidden = true;
      return;
    }
    this.#leaveTimer = window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, LEAVE_MS);
  }

  /**
   * @param {string} variantTitle
   */
  #showAdded(variantTitle) {
    const cfg = config();
    this.#render({
      state: 'added',
      status: cfg.added || 'Added to cart',
      detail: variantTitle,
      link: { href: cfg.cartUrl || '/cart', label: cfg.viewCart || 'View cart', cart: true },
    });
    this.#setTrigger('added');
    this.#autoDismiss = true;
    this.#startTimer(this.#touchUsed || window.matchMedia('(pointer: coarse)').matches ? DWELL_LONG_MS : DWELL_MS);
  }

  /**
   * @param {number} token
   * @param {number} addedCents
   * @param {string} variantTitle
   * @param {any} [knownCart]
   */
  async #fillShipping(token, addedCents, variantTitle, knownCart = null) {
    const cfg = config();
    const threshold = Number(cfg.threshold) || 0;
    let announced = false;
    const announceBase = () => {
      if (announced) return;
      announced = true;
      this.#announceAdded(variantTitle, '', true);
    };

    if (threshold <= 0) {
      announceBase();
      return;
    }

    const fallback = window.setTimeout(announceBase, ANNOUNCE_FALLBACK_MS);
    const cart = knownCart || (await fetchCart());
    window.clearTimeout(fallback);

    if (token !== this.#addToken || this.getAttribute('data-state') !== 'added' || typeof cart?.total_price !== 'number') {
      announceBase();
      return;
    }

    const total = cart.total_price;
    const before = Math.max(0, total - addedCents);
    if (total >= threshold && before >= threshold) {
      announceBase();
      return;
    }

    const unlocked = total >= threshold;
    const amount = unlocked ? '' : money(threshold - total);
    const ship = unlocked ? cfg.shipSuccess || '' : amount ? cfg.shipMessage || '' : '';
    if (!ship) {
      announceBase();
      return;
    }

    if (unlocked) this.setAttribute('data-state', 'unlocked');
    this.#setShipping(
      ship,
      amount,
      Math.min(100, Math.round((before / threshold) * 100)),
      unlocked ? 100 : Math.min(100, Math.round((total / threshold) * 100))
    );
    if (this.#autoDismiss) this.#startTimer(DWELL_LONG_MS);

    if (!announced) {
      announced = true;
      this.#announceAdded(variantTitle, amount ? ship.split('[amount]').join(amount) : ship, true);
    }
  }

  /**
   * @param {string} variantTitle
   * @param {string} shipText
   * @param {boolean} [withLink]
   */
  #announceAdded(variantTitle, shipText, withLink = false) {
    const cfg = config();
    const title = this.dataset.productTitle || '';
    const added = cfg.added || 'Added to cart';
    const link = withLink && this.hasAttribute('data-state') ? cfg.viewCart || 'View cart' : '';
    announce([`${title}: ${added}`, variantTitle, shipText, link].filter(Boolean).join('. '), {
      delay: ANNOUNCE_DELAY_MS,
    });
  }

  /**
   * @param {'soldout' | 'error'} kind
   * @param {string} variantId
   * @param {string} description
   */
  #fail(kind, variantId, description) {
    const cfg = config();
    const title = this.dataset.productTitle || '';

    if (kind === 'soldout') {
      this.#soldOut = !this.refs.trigger?.hasAttribute('data-choose');
      const base = this.dataset.productUrl || '';
      const href = base ? `${base}${base.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variantId)}` : '';
      const status = cfg.soldOut || 'Sold out';
      this.#render({
        state: 'soldout',
        status,
        detail: '',
        link: href ? { href, label: cfg.notify || 'Notify me', cart: false } : null,
      });
      this.#setTrigger(this.#soldOut ? 'soldout' : 'default');
      announce(`${title}: ${status}`, { politeness: 'assertive', delay: ANNOUNCE_DELAY_MS });
      return;
    }

    const status = description || cfg.error || 'Could not add the item. Please try again.';
    this.#render({ state: 'error', status, detail: '', link: null });
    this.#setTrigger('default');
    announce(`${title}: ${status}`, { politeness: 'assertive', delay: ANNOUNCE_DELAY_MS });
  }

  /**
   * @param {string} variantId
   * @returns {Promise<boolean>}
   */
  async #variantSoldOut(variantId) {
    const base = (this.dataset.productUrl || '').split('?')[0];
    if (!base) return false;
    try {
      const response = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const product = await response.json();
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const variant = variants.find((/** @type {any} */ v) => String(v.id) === String(variantId));
      return !!variant && variant.available === false;
    } catch (error) {
      return false;
    }
  }

  /** @returns {boolean} */
  #ensurePanel() {
    const current = this.refs.panel;
    if (current && this.contains(current)) return true;
    const template = document.getElementById('CaQuickAddPanelTemplate');
    if (!(template instanceof HTMLTemplateElement)) return false;
    const fragment = /** @type {DocumentFragment} */ (document.importNode(template.content, true));
    const scope = this.id.replace(/^CaQuickAdd-/, '');
    const panel = fragment.querySelector('.ca-quick-add__panel');
    const status = fragment.querySelector('.ca-quick-add__status');
    if (!panel || !status) return false;
    panel.id = `CaQuickAddPanel-${scope}`;
    status.id = `CaQuickAddStatus-${scope}`;
    panel.setAttribute('aria-labelledby', status.id);
    this.append(fragment);
    this.updatedCallback();
    return !!this.refs.panel;
  }

  /**
   * @param {{ state: string, status: string, detail: string, link: { href: string, label: string, cart: boolean } | null }} view
   */
  #render(view) {
    if (!this.#ensurePanel()) return;
    const { panel, status, detail, ship, track, link } = this.refs;
    if (!panel) return;

    const active = CaQuickAddComponent.active;
    if (active && active !== this) active.dismiss(true);

    window.clearTimeout(this.#leaveTimer);
    this.setAttribute('data-state', view.state);

    if (status) status.textContent = view.status;
    if (detail) {
      detail.textContent = view.detail;
      detail.hidden = !view.detail;
    }
    if (ship) {
      ship.replaceChildren();
      ship.hidden = true;
    }
    if (track) track.hidden = true;
    if (link) {
      if (view.link) {
        link.href = view.link.href;
        link.textContent = view.link.label;
        link.dataset.cart = view.link.cart ? 'true' : 'false';
        link.hidden = false;
      } else {
        link.hidden = true;
      }
    }

    this.#item()?.classList.add(OPEN_CLASS);
    CaQuickAddComponent.active = this;

    panel.hidden = false;
    void panel.offsetWidth;
    panel.classList.add('is-open');
  }

  /**
   * @param {string} text
   * @param {string} amount
   * @param {number} fromPct
   * @param {number} toPct
   */
  #setShipping(text, amount, fromPct, toPct) {
    const { ship, track, fill } = this.refs;
    if (ship) {
      ship.replaceChildren();
      const parts = amount ? text.split('[amount]') : [text];
      parts.forEach((part, index) => {
        if (index > 0) {
          const value = document.createElement('span');
          value.className = 'ca-quick-add__amount';
          value.textContent = amount;
          ship.append(value);
        }
        if (part) ship.append(document.createTextNode(part));
      });
      ship.hidden = false;
    }
    if (track && fill) {
      fill.style.transform = `scaleX(${Math.max(0, fromPct) / 100})`;
      track.hidden = false;
      void fill.offsetWidth;
      requestAnimationFrame(() => {
        fill.style.transform = `scaleX(${toPct / 100})`;
      });
    }
  }

  /**
   * @param {'default' | 'added' | 'soldout'} state
   */
  #setTrigger(state) {
    const trigger = this.refs.trigger;
    if (!trigger) return;
    const label = /** @type {HTMLElement | null} */ (trigger.querySelector('.ca-quick-add__label'));
    if (!trigger.dataset.defaultAria) trigger.dataset.defaultAria = trigger.getAttribute('aria-label') || '';
    if (label && !trigger.dataset.defaultLabel) trigger.dataset.defaultLabel = label.textContent || '';

    const cfg = config();
    const title = this.dataset.productTitle || '';
    trigger.dataset.state = state;

    if (state === 'default') {
      if (this.#busy) trigger.setAttribute('aria-disabled', 'true');
      else trigger.removeAttribute('aria-disabled');
      trigger.setAttribute('aria-label', trigger.dataset.defaultAria || '');
      if (label) label.textContent = trigger.dataset.defaultLabel || '';
      return;
    }

    const text = state === 'added' ? cfg.addedShort || 'Added' : cfg.soldOut || 'Sold out';
    trigger.setAttribute('aria-disabled', 'true');
    trigger.setAttribute('aria-label', `${text}: ${title}`);
    if (label) label.textContent = text;
  }

  #onFocusIn = () => {
    if (this.#holdsFocus()) this.#pause();
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (event.target === this.refs.trigger) this.#quietTrigger = false;
    const next = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (next && this.contains(next)) return;
    requestAnimationFrame(() => this.#resume());
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.target === this.refs.trigger) this.#quietTrigger = false;
    if (event.key !== 'Escape' || !this.hasAttribute('data-state')) return;
    event.preventDefault();
    this.dismiss(true);
    this.focusTrigger();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse' || event.target !== this.refs.panel) return;
    this.#hovering = true;
    this.#pause();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse' || event.target !== this.refs.panel) return;
    this.#hovering = false;
    this.#resume();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    const target = event.target instanceof Node ? event.target : null;
    this.#touchUsed = event.pointerType !== 'mouse';
    if (target && this.refs.trigger?.contains(target)) this.#quietTrigger = true;
    if (this.#touchUsed && target && this.refs.panel?.contains(target)) this.#hold();
  };

  /**
   * @param {Event} event
   */
  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#release();
    this.#resume();
  };

  #hold() {
    if (!this.#autoDismiss) return;
    this.#pause();
    if (this.#held) return;
    this.#held = true;
    document.addEventListener('pointerdown', this.#onOutsidePointer, true);
  }

  #release() {
    if (!this.#held) return;
    this.#held = false;
    document.removeEventListener('pointerdown', this.#onOutsidePointer, true);
  }

  /** @returns {boolean} */
  #holdsFocus() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return false;
    if (this.refs.panel?.contains(active)) return true;
    return !(active === this.refs.trigger && this.#quietTrigger);
  }

  /** @returns {boolean} */
  #paused() {
    return this.#hovering || this.#held || this.#holdsFocus();
  }

  /**
   * @param {number} ms
   */
  #startTimer(ms) {
    window.clearTimeout(this.#timer);
    this.#timer = 0;
    this.#remaining = ms;
    if (this.#paused()) return;
    this.#deadline = Date.now() + ms;
    this.#timer = window.setTimeout(this.#expire, ms);
  }

  #expire = () => {
    this.#timer = 0;
    if (this.#paused()) {
      this.#remaining = RESUME_MIN_MS;
      return;
    }
    this.dismiss();
  };

  #pause() {
    if (!this.#timer) return;
    window.clearTimeout(this.#timer);
    this.#timer = 0;
    this.#remaining = Math.max(0, this.#deadline - Date.now());
  }

  #resume() {
    if (!this.#autoDismiss || this.#timer || this.#paused()) return;
    const ms = Math.max(this.#remaining, RESUME_MIN_MS);
    this.#deadline = Date.now() + ms;
    this.#timer = window.setTimeout(this.#expire, ms);
  }

  /** @returns {HTMLElement | null} */
  #item() {
    return this.closest('.ca-quick-add-host');
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {string} mode
   */
  #track(variantId, quantity, mode) {
    try {
      const analytics = /** @type {any} */ (window).Shopify?.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      analytics.publish('ca_quick_add', {
        product_id: this.dataset.productId || '',
        variant_id: String(variantId),
        quantity,
        mode,
        surface: this.dataset.surface || (typeof Theme !== 'undefined' && Theme.template?.name) || '',
      });
    } catch (error) {
      return;
    }
  }
}

if (!customElements.get('ca-quick-add-component')) {
  customElements.define('ca-quick-add-component', CaQuickAddComponent);
}

ensureAnnouncer();
